import * as React from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField, Typography } from '@mui/material';
import { getFlashBetSettings, updateFlashBetSettings } from '../Api';
import FlashBetSettingsDto from '../models/dtos/FlashBetSettingsDto';

function parseLocaleInt(raw: string): number {
    const t = raw.trim().replace(/\s/g, '');
    if (t.includes(',') || t.includes('.')) return Number.NaN;
    return parseInt(t, 10);
}

export default function FlashBetSettingsPage() {
    const [loading, setLoading] = React.useState(true);
    const [matchTimerSpeedMultiplier, setMatchTimerSpeedMultiplier] = React.useState('5');
    const [totalMatchRealSeconds, setTotalMatchRealSeconds] = React.useState('20');
    const [gapBetweenQuestionsMs, setGapBetweenQuestionsMs] = React.useState('1000');
    const [clientQuestionCountdownSeconds, setClientQuestionCountdownSeconds] = React.useState('12');
    const [endingPhaseMatchMinutes, setEndingPhaseMatchMinutes] = React.useState('15');
    const [revealTeamsDelayMs, setRevealTeamsDelayMs] = React.useState('1500');
    const [resultDialogDelayMs, setResultDialogDelayMs] = React.useState('1500');
    const [goalSoundLeadMs, setGoalSoundLeadMs] = React.useState('1000');
    const [lastQuestionMinute, setLastQuestionMinute] = React.useState('75');
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogTitle, setDialogTitle] = React.useState('');
    const [dialogMsg, setDialogMsg] = React.useState('');

    const applyDto = (dto: FlashBetSettingsDto | null) => {
        if (dto != null) {
            setMatchTimerSpeedMultiplier(String(dto.matchTimerSpeedMultiplier ?? 5));
            setTotalMatchRealSeconds(String(dto.totalMatchRealSeconds ?? 20));
            setGapBetweenQuestionsMs(String(dto.gapBetweenQuestionsMs ?? 1000));
            setClientQuestionCountdownSeconds(String(dto.clientQuestionCountdownSeconds ?? 12));
            setEndingPhaseMatchMinutes(String(dto.endingPhaseMatchMinutes ?? 15));
            setRevealTeamsDelayMs(String(dto.revealTeamsDelayMs ?? 1500));
            setResultDialogDelayMs(String(dto.resultDialogDelayMs ?? 1500));
            setGoalSoundLeadMs(String(dto.goalSoundLeadMs ?? 1000));
            setLastQuestionMinute(String(dto.lastQuestionMinute ?? 75));
        }
    };

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const dto = await getFlashBetSettings();
            if (!cancelled) {
                applyDto(dto);
                setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const openDialog = (title: string, message: string) => {
        setDialogTitle(title);
        setDialogMsg(message);
        setDialogOpen(true);
    };

    const onSave = async () => {
        const v = {
            m: parseLocaleInt(matchTimerSpeedMultiplier),
            t: parseLocaleInt(totalMatchRealSeconds),
            g: parseLocaleInt(gapBetweenQuestionsMs),
            c: parseLocaleInt(clientQuestionCountdownSeconds),
            e: parseLocaleInt(endingPhaseMatchMinutes),
            r: parseLocaleInt(revealTeamsDelayMs),
            d: parseLocaleInt(resultDialogDelayMs),
            o: parseLocaleInt(goalSoundLeadMs),
            l: parseLocaleInt(lastQuestionMinute),
        };
        if (Object.values(v).some(Number.isNaN)) {
            openDialog('Error', 'Enter whole numbers for all fields.');
            return;
        }
        const payload: FlashBetSettingsDto = {
            matchTimerSpeedMultiplier: v.m,
            totalMatchRealSeconds: v.t,
            gapBetweenQuestionsMs: v.g,
            clientQuestionCountdownSeconds: v.c,
            endingPhaseMatchMinutes: v.e,
            revealTeamsDelayMs: v.r,
            resultDialogDelayMs: v.d,
            goalSoundLeadMs: v.o,
            lastQuestionMinute: v.l,
        };
        const ok = await updateFlashBetSettings(payload);
        if (!ok) {
            openDialog('Error', 'Failed to save. Check server logs and API configuration.');
            return;
        }
        const dto = await getFlashBetSettings();
        applyDto(dto);
        openDialog('Saved', 'Flash Bet UX settings were saved to the main API (qbet_app_settings). Games server picks them up on the next cache refresh (~1 min).');
    };

    const speedN = parseLocaleInt(matchTimerSpeedMultiplier);
    const wallN = parseLocaleInt(totalMatchRealSeconds);
    const productOk = !Number.isNaN(speedN) && !Number.isNaN(wallN);
    const maxMinuteInMainPhase = productOk ? speedN * wallN : null;
    const minWallFor75 = productOk && speedN > 0 ? Math.ceil(75 / speedN) : null;
    const minWallFor90 = productOk && speedN > 0 ? Math.ceil(90 / speedN) : null;

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Typography variant="h4">Flash Bet — client timing (Android)</Typography>
            <Typography variant="body2" color="text.secondary">
                Marketing / UX timers for the Flash Bet match flow. Stored under keys flash_bet.* in qbet_app_settings.
                The games service reads this bundle and includes values on each Flash Bet session response.
            </Typography>
            <Alert severity="info" sx={{ maxWidth: 'md' }}>
                <Typography variant="subtitle2" gutterBottom>How match speed and wall time work together</Typography>
                <Typography variant="body2" component="div">
                    While the match clock runs (not paused on a question), displayed minute ≈ <strong>elapsed real seconds × match timer speed</strong>,
                    capped by <strong>wall seconds</strong> for that phase. Roughly: <strong>max minute ≈ speed × wall seconds</strong> before the ending phase.
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                    <strong>Rule of thumb:</strong> use <code>wall seconds ≥ ⌈75 ÷ speed⌉</code> so the timer can reach the third question (often 75′).
                    For a full 0′→90′ feel in that segment: <code>wall seconds ≥ ⌈90 ÷ speed⌉</code>.
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                    <strong>Examples:</strong> speed <strong>5</strong> + wall <strong>20</strong> → 5×20=100 match-minutes of “run” (enough for 90′).
                    Speed <strong>2</strong> + wall <strong>20</strong> → only 40′ — too low; use wall <strong>≥38</strong> for 75′ or <strong>≥45</strong> for 90′.
                </Typography>
            </Alert>
            {productOk && maxMinuteInMainPhase != null && maxMinuteInMainPhase < 75 && (
                <Alert severity="warning" sx={{ maxWidth: 'md' }}>
                    Current speed × wall seconds = <strong>{maxMinuteInMainPhase}</strong>′ — below 75′. Questions tied to 45′/75′ may not unlock in time.
                    {minWallFor75 != null && (
                        <> Try raising <strong>wall seconds</strong> to at least <strong>{minWallFor75}</strong> (for 75′) or <strong>{minWallFor90 ?? '—'}</strong> (for 90′).</>
                    )}
                </Alert>
            )}
            {loading ? (
                <Box><CircularProgress /></Box>
            ) : (
                <Grid container spacing={2} maxWidth="md">
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Match timer speed (× min per real sec)"
                            type="number"
                            fullWidth
                            value={matchTimerSpeedMultiplier}
                            onChange={(e) => setMatchTimerSpeedMultiplier(e.target.value)}
                            inputProps={{ min: 1 }}
                            helperText="Example: 5 → each real second advances 5′ on the match clock. Lower = slower story (needs more wall seconds)."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Wall seconds for 0′→90′ progression"
                            type="number"
                            fullWidth
                            value={totalMatchRealSeconds}
                            onChange={(e) => setTotalMatchRealSeconds(e.target.value)}
                            inputProps={{ min: 5 }}
                            helperText={productOk && speedN > 0
                                ? `With speed ${speedN}, product is ${speedN * wallN}′. Min wall for 75′: ⌈75/${speedN}⌉ = ${Math.ceil(75 / speedN)}; for 90′: ${Math.ceil(90 / speedN)}.`
                                : 'Cap on real seconds for the main run before the post-answers ending phase. Must be large enough vs speed (see blue box).'}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Gap between questions (ms)"
                            type="number"
                            fullWidth
                            value={gapBetweenQuestionsMs}
                            onChange={(e) => setGapBetweenQuestionsMs(e.target.value)}
                            inputProps={{ min: 0 }}
                            helperText="Pause after an answer before the next bet UI. Example: 1000 ms = 1 s of “match in progress” only."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Question countdown on device (seconds)"
                            type="number"
                            fullWidth
                            value={clientQuestionCountdownSeconds}
                            onChange={(e) => setClientQuestionCountdownSeconds(e.target.value)}
                            inputProps={{ min: 5 }}
                            helperText="Timer shown while the question is visible. Keep a few seconds below the server session timeout per sport (e.g. if server allows 90 s, 12 s on device is safe headroom for the network)."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Ending phase — extra minutes to 90′"
                            type="number"
                            fullWidth
                            value={endingPhaseMatchMinutes}
                            onChange={(e) => setEndingPhaseMatchMinutes(e.target.value)}
                            inputProps={{ min: 1 }}
                            helperText="After all 3 answers: how many match-minutes to simulate from “last question baseline” up to 90′. Example: 15 → animate 75′→90′ (if baseline is 75)."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Reveal teams delay (ms)"
                            type="number"
                            fullWidth
                            value={revealTeamsDelayMs}
                            onChange={(e) => setRevealTeamsDelayMs(e.target.value)}
                            inputProps={{ min: 0 }}
                            helperText="How long the “revealing teams” beat lasts before the simulated match starts. Example: 1500 ms = 1.5 s."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Result / details dialog delay (ms)"
                            type="number"
                            fullWidth
                            value={resultDialogDelayMs}
                            onChange={(e) => setResultDialogDelayMs(e.target.value)}
                            inputProps={{ min: 0 }}
                            helperText="Wait before auto-opening the details / result dialog. Example: 1500 ms after the result screen is ready."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Goal sound lead time (ms)"
                            type="number"
                            fullWidth
                            value={goalSoundLeadMs}
                            onChange={(e) => setGoalSoundLeadMs(e.target.value)}
                            inputProps={{ min: 0 }}
                            helperText="Play goal SFX this many ms before the displayed minute hits the goal minute. Example: 1000 = 1 s early so audio feels synced."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Last question baseline minute (e.g. 75)"
                            type="number"
                            fullWidth
                            value={lastQuestionMinute}
                            onChange={(e) => setLastQuestionMinute(e.target.value)}
                            inputProps={{ min: 0, max: 89 }}
                            helperText="Starting minute for the final run to 90′ after all answers. Align with sport config (often 75′ for the 3rd question). If sport uses different timings, match this to the last question minute."
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" onClick={onSave}>Save</Button>
                    </Grid>
                </Grid>
            )}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent><Typography>{dialogMsg}</Typography></DialogContent>
                <DialogActions><Button onClick={() => setDialogOpen(false)}>OK</Button></DialogActions>
            </Dialog>
        </Stack>
    );
}
